import React from "react"
import { TableColumn } from "resha"
import {
    APICollaborationModel,
    CollaboratorStatus,
    CollaboratorRole
} from "../../../../../../Models/API/CollaboratorModel"
import {
    ProjectRole,
    canEditBy
} from "../../../../../../Models/Client/ProjectRole"
import { invertStatus } from "./ColumnCommon"
import { Help, IconEdit } from "dash-styled"
import { ReactComponent as IconClose } from "icons/lib/close-icon.svg"
import { AsyncBinaryChoice } from "../../../../../Common/AsyncBinaryChoice/AsyncBinaryChoice"

export const reviewerColumns: (args: {
    myEmail: string
    roles: ProjectRole[]
    handleToggleActive?: (
        id: number,
        status: CollaboratorStatus
    ) => Promise<void>
    viewPermissions: () => void
    changeRole?: (id: number, email: string, role: CollaboratorRole) => void
    deleteCollaborator?: (id: number) => void
}) => TableColumn<APICollaborationModel>[] = ({
    myEmail,
    roles,
    handleToggleActive,
    viewPermissions,
    changeRole,
    deleteCollaborator
}) => [
    {
        className: "truncate",
        renderHeader: () => "Email",
        render: ({ invitedEmail }) => (
            <span className="font-medium">
                {invitedEmail}
                {myEmail === invitedEmail ? (
                    <span className="ml-1 italic">(you)</span>
                ) : (
                    ""
                )}
            </span>
        )
    },
    {
        renderHeader: () => "Role",
        render: model => {
            const handleClick =
                changeRole === undefined
                    ? undefined
                    : () =>
                          changeRole(
                              model.id,
                              model.invitedEmail,
                              model.collaboratorRole
                          )

            const isRoleOverridden =
                model.orgRole === "OWNER" && model.collaboratorRole !== "OWNER"

            return (
                <div className="flex">
                    <button
                        onClick={viewPermissions}
                        className={`p-0 capitalize text-grape-500 ${
                            isRoleOverridden ? "line-through" : ""
                        }`}
                    >
                        {model.collaboratorRole.toLowerCase()}
                    </button>
                    {isRoleOverridden && (
                        <>
                            <span className="ml-1">Owner</span>
                            <Help
                                className="ml-1"
                                text="Organization owner has always owner-level access to projects within the organization"
                                tooltipPlacement="right"
                            />
                        </>
                    )}
                    {handleClick === undefined ||
                    canEditBy(model, myEmail, roles) ? (
                        <button
                            onClick={handleClick}
                            className="text-smoke-350"
                        >
                            <IconEdit />
                        </button>
                    ) : null}
                </div>
            )
        }
    },
    {
        renderHeader: () => "Activated",
        render: model => {
            const handleChange =
                handleToggleActive === undefined
                    ? undefined
                    : () =>
                          handleToggleActive(
                              model.id,
                              invertStatus(model.collaboratorStatus)
                          )
            return (
                <AsyncBinaryChoice
                    interactive={true}
                    isChecked={
                        model.collaboratorStatus === CollaboratorStatus.ACTIVE
                    }
                    onChange={handleChange}
                    className="-ml-1"
                    buttonColor="violet"
                    size="xs"
                    disabled={
                        handleChange === undefined ||
                        !canEditBy(model, myEmail, roles)
                    }
                />
            )
        }
    },
    {
        className: "text-right",
        renderHeader: () => null,
        render: model =>
            canEditBy(model, myEmail, roles) && (
                <button
                    data-testid="delete-collaborator"
                    className="text-smoke-350 align-middle"
                    onClick={() => deleteCollaborator?.(model.id)}
                >
                    <IconClose />
                    <span className="sr-only">Delete</span>
                </button>
            )
    }
]
